import { useEffect, useState } from "react";

/** Temporary debug overlay that pins to the visible bottom of the
 *  layout and prints every viewport dimension iOS standalone PWA
 *  exposes, plus a 1px red marker pinned via `position: fixed; bottom: 0`
 *  so we can SEE where "bottom: 0" actually lands.
 *
 *  Enable with `localStorage.setItem("viewport-debug", "1")` then
 *  reload. Disable with `localStorage.removeItem("viewport-debug")`. */
export function ViewportDebug() {
  const [enabled] = useState(
    () =>
      typeof localStorage !== "undefined" &&
      localStorage.getItem("viewport-debug") === "1",
  );
  const [snap, setSnap] = useState(() => readSnap());

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => setSnap(readSnap()), 500);
    const onResize = () => setSnap(readSnap());
    window.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("resize", onResize);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      {/* Pinned to "fixed positioning viewport" bottom = where iOS
          stops considering as bottom edge. Shows whether the bug is
          present (gap) or absent (sits at literal screen bottom). */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          height: 2,
          background: "red",
          zIndex: 999998,
          pointerEvents: "none",
        }}
      />
      {/* The same marker but via dvh — should match physical bottom
          on devices that honour viewport-fit=cover. */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "calc(100dvh - 2px)",
          height: 2,
          background: "lime",
          zIndex: 999998,
          pointerEvents: "none",
        }}
      />
      <pre
        style={{
          position: "fixed",
          left: 8,
          top: "calc(60px + env(safe-area-inset-top, 0px))",
          margin: 0,
          padding: "8px 10px",
          background: "rgba(0,0,0,0.78)",
          color: "#9ef9ff",
          font: "11px/1.35 ui-monospace, monospace",
          borderRadius: 6,
          zIndex: 999999,
          pointerEvents: "none",
          whiteSpace: "pre",
        }}
      >
        {snap}
      </pre>
    </>
  );
}

function readSnap(): string {
  const sad = getComputedStyle(document.documentElement);
  const env = (s: string) =>
    sad.getPropertyValue(s).trim() || "(empty)";
  const win = `win  ${window.innerWidth}×${window.innerHeight}`;
  const scr = `scr  ${window.screen.width}×${window.screen.height}`;
  const doc = `doc  ${document.documentElement.clientWidth}×${document.documentElement.clientHeight}`;
  const body = `body ${document.body.clientWidth}×${document.body.clientHeight}`;
  const vv = window.visualViewport
    ? `vv   ${Math.round(window.visualViewport.width)}×${Math.round(window.visualViewport.height)} off ${Math.round(window.visualViewport.offsetTop)}`
    : "vv   n/a";
  const insets = `sat  ${env("--sat")} sar ${env("--sar")} sab ${env("--sab")} sal ${env("--sal")}`;
  const dvh = `dvh  ${getCSSValue("100dvh")}`;
  const vh = `vh   ${getCSSValue("100vh")}`;
  const standalone = window.matchMedia("(display-mode: standalone)").matches
    ? "STANDALONE"
    : "browser";
  return [standalone, win, scr, doc, body, vv, dvh, vh, insets].join("\n");
}

function getCSSValue(expr: string): string {
  const el = document.createElement("div");
  el.style.cssText = `position:fixed;left:-9999px;height:${expr}`;
  document.body.appendChild(el);
  const h = el.getBoundingClientRect().height;
  el.remove();
  return `${h}px`;
}
